import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useInvitations, checkEmailAvailability } from '@/hooks/useInvitations';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
interface InviteDriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export const InviteDriverDialog = ({
  open,
  onOpenChange
}: InviteDriverDialogProps) => {
  const {
    t
  } = useTranslation();
  const {
    createInvitation,
    sendInvitationEmail
  } = useInvitations();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [driverRoleId, setDriverRoleId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  useEffect(() => {
    const fetchRoleAndCompany = async () => {
      // Get driver role ID
      const {
        data: roleData
      } = await supabase.from('roles').select('id').eq('code', 'driver').single();
      if (roleData) {
        setDriverRoleId(roleData.id);
      }

      // Get company name
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (user) {
        const {
          data: userData
        } = await supabase.from('users').select('companies (name)').eq('id', user.id).single();
        if (userData?.companies) {
          setCompanyName(userData.companies.name);
        }
      }
    };
    if (open) {
      fetchRoleAndCompany();
    }
  }, [open]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !firstName || !lastName || !driverRoleId) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setIsSubmitting(true);
    try {
      // Vérification préalable de l'adresse e-mail
      const availability = await checkEmailAvailability(email);

      if (availability.email_exists) {
        toast.error(
          t('invite.emailAlreadyUsed', {
            company: availability.company_name || '—',
            role: availability.role_code || '—',
          })
        );
        return;
      }

      if (availability.pending_invitation) {
        toast.error(
          t('invite.invitationAlreadyPending', {
            company: availability.pending_company || '—',
          })
        );
        return;
      }

      // Create invitation
      const invitation = await createInvitation.mutateAsync({
        email,
        role_id: driverRoleId,
        first_name: firstName,
        last_name: lastName,
        phone
      });

      // Send email
      await sendInvitationEmail(invitation, companyName, 'Chauffeur');
      toast.success(`Invitation envoyée à ${email}`);
      onOpenChange(false);

      // Reset form
      setEmail('');
      setFirstName('');
      setLastName('');
      setPhone('');
    } catch (error: any) {
      console.error('Error inviting driver:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            {t('fleet.dialogs.inviteDriver')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.email')} {t('fleet.dialogs.required')}
            </Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="chauffeur@exemple.fr" className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans']" />
          </div>

          {/* Prénom */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.firstName')} {t('fleet.dialogs.required')}
            </Label>
            <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jean" className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans']" />
          </div>

          {/* Nom */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.lastName')} {t('fleet.dialogs.required')}
            </Label>
            <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Dupont" className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans']" />
          </div>

          {/* Téléphone */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.phone')}
            </Label>
            <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans']" />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#DBE2EA] text-[#5A6B7A] hover:bg-[#EBF4F8] font-['Funnel_Sans']">
              {t('fleet.dialogs.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg transition-colors duration-200">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('fleet.dialogs.sendInvitation')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>;
};